import { get as getCookie } from 'js-cookie';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, UseFormReset, useForm } from 'react-hook-form';
import { SWRConfiguration } from 'swr';

import { useLoggedInEmployeeProfile } from '~/components/Auth';
import { LEGACY_COOKIE } from '~/data/cookies/';
import {
  useCustomDataAttributes,
  useCustomEmployeeData,
} from '~/data/customData';
import { useEmployee } from '~/data/employee';
import { useAllEnums } from '~/data/enums';
import {
  getCountryByLegalEntity,
  useAllLegalEntities,
} from '~/data/legalEntity';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  EditCustomField,
  ReadCustomField,
  jsEditCustomFieldData,
  readCustomData,
} from '~/legacy-components/CustomDataFields';

import { AnswerPreProcess } from '../../../types';

import { saveCustomFieldValues } from './data';

export type UseCustomDataOutput = {
  isLoading: boolean;
  error: string;
  canEdit: boolean;
  readFieldData: ReadCustomField[];
  editFieldData: EditCustomField[];
  handleFormSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  control: Control<AnswerPreProcess>;
  reset: UseFormReset<AnswerPreProcess>;
  editMode: boolean;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
};

type PropsType = {
  employeeId: number;
};

export const useCustomData = (props: PropsType): UseCustomDataOutput => {
  const { employeeId } = props;

  const iRehire = getCookie(LEGACY_COOKIE.REHIRE) === 'active';

  // Load data
  const userProfile = useLoggedInEmployeeProfile();
  const {
    isSelectedEmployeeLoggedInUser,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  const {
    legalEntities,
    isLoading: legalEntitiesLoading,
    hasData: legalEntityHasData,
  } = useAllLegalEntities();

  const { allEnums } = useAllEnums();

  const {
    employee: { empBasicData, empPosition, empPositionPending },
  } = useEmployee();

  // States
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState<string>('');
  const [readFieldData, setReadFieldData] = useState<ReadCustomField[]>([]);
  const [editFieldData, setEditFieldData] = useState<EditCustomField[]>([]);

  // Load Custom Data
  const swrConfig: SWRConfiguration = {
    revalidateOnFocus: false,
    revalidateIfStale: false,
  };

  const {
    data: customEmployeeData,
    isLoading: isLoadingCustomEmployeeData,
    mutate: mutateCustomEmployeeData,
  } = useCustomEmployeeData(employeeId, swrConfig);
  const {
    data: customDataAttributes,
    isLoading: isLoadingCustomDataAttributes,
    mutate: mutateCustomDataAttributes,
  } = useCustomDataAttributes(swrConfig);

  const refreshCustomData = useCallback(() => {
    mutateCustomEmployeeData();
    mutateCustomDataAttributes();
  }, [mutateCustomEmployeeData, mutateCustomDataAttributes]);

  const isLoading =
    isLoadingCustomDataAttributes ||
    isLoadingCustomEmployeeData ||
    (legalEntitiesLoading && !legalEntityHasData);

  const { legalEntityCountry } = useMemo(() => {
    const legalEntityId =
      ['NEW', 'PENDING'].includes(empBasicData.fEmpStatus) && empPositionPending
        ? empPositionPending.fEmpLegalEntity
        : empPosition.fEmpLegalEntity;

    const legalEntityCountry = getCountryByLegalEntity(
      legalEntityId,
      legalEntities,
    );

    return {
      legalEntityCountry,
    };
  }, [empBasicData, empPositionPending, empPosition, legalEntities]);

  const { customFieldAttributesHash } = useMemo(() => {
    const customFieldAttributesHash = Object.assign(
      {},
      ...customDataAttributes!.map((field) => ({
        [field.fCustomDataAttributeName]: field,
      })),
    );

    return { customFieldAttributesHash };
  }, [customDataAttributes]);

  const allowedRoles: Roles[] = [
    Roles.HR_STAFF,
    Roles.HR_SALARY,
    Roles.HR_PEOP,
    Roles.HR_PEOP_DEV,
    Roles.HR_DEV,
    Roles.HR_ADMIN,
    Roles.MANAGER,
    Roles.ALT_MANAGER,
    Roles.DEPUTY_MANAGER,
  ];
  const canEdit: boolean =
    isSelectedEmployeeLoggedInUser || hasRoleAccessOverEmployee(allowedRoles);

  // Form
  const { handleSubmit, control, reset } = useForm<AnswerPreProcess>();

  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit((data) => {
      return saveCustomFieldValues(String(employeeId), data).then((result) => {
        setEditMode(false);
        refreshCustomData();
        return result;
      });
    });
  }, [employeeId, handleSubmit, refreshCustomData]);

  const shouldRefreshFieldData = useMemo(() => {
    return !isLoading && !iRehire;
  }, [iRehire, isLoading]);

  useEffect(() => {
    try {
      if (shouldRefreshFieldData) {
        setReadFieldData(
          readCustomData(
            'EMPLOYEE',
            customFieldAttributesHash,
            customEmployeeData!,
            legalEntityCountry,
            allEnums,
          ),
        );
        setEditFieldData(
          jsEditCustomFieldData({
            strEntity: 'EMPLOYEE',
            customFieldAttributesHash,
            customFields: customEmployeeData!,
            legalEntityCountry,
            allEnums,
          }),
        );
      }
    } catch (err: unknown) {
      setError(`${err}`);
    }
  }, [
    allEnums,
    customEmployeeData,
    legalEntityCountry,
    customFieldAttributesHash,
    userProfile.legalEntity.id,
    shouldRefreshFieldData,
  ]);

  return {
    reset,
    isLoading,
    error,
    canEdit,
    readFieldData,
    editFieldData,
    handleFormSubmit,
    control,
    editMode,
    setEditMode,
  };
};
